// Updated React component with enhanced Subscription History
import React, { useState, useEffect } from 'react';
import './Subscription.css';
import api from "../Api";
import StripeCheckout from './StripeCheckout';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});

  // Helper function to get auth token from localStorage
  const getAuthToken = () => {
    const token = localStorage.getItem('technician_token');
    if (!token) {
      console.warn('No technician token found in localStorage');
      return null;
    }
    return token;
  };

  // Fetch all plans (no authentication required)
  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/web/subscriptions', {
        headers: {
          'accept': 'application/json',
        },
      });

      const data = response.data;
      console.log('Fetched plans data:', data);
      if (data && data.length > 0 && data[0].plans) {
        setSubscriptionId(data[0]._id);
        setPlans(data[0].plans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans');
    }
  };

  // Fetch current subscription status
  const fetchCurrentSubscriptionStatus = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.warn('No token available for subscription status');
        return;
      }

      const response = await api.get('/api/web/subscription-status', {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = response.data;
      if (result.success && result.data) {
        setCurrentSubscription(result.data);
      } else {
        setCurrentSubscription(null);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setCurrentSubscription(null);
    }
  };

  // Fetch user's subscription history
  const fetchMySubscriptions = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.warn('No token available for subscriptions');
        return;
      }

      const response = await api.get('/api/web/my-subscriptions', {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = response.data;
      if (result.success) {
        setMySubscriptions(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setMySubscriptions([]);
    }
  };

  // Load all data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchPlans(),
        fetchCurrentSubscriptionStatus(),
        fetchMySubscriptions(),
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Get the latest active subscription
  const getLatestActiveSubscription = () => {
    const activeSubscription = mySubscriptions.find(sub => sub.status === 'active');
    if (activeSubscription) {
      return activeSubscription;
    }
    
    if (currentSubscription && currentSubscription.status === 'active') {
      return currentSubscription;
    }
    
    return null;
  };

  // Get the latest subscription for display
  const getLatestSubscription = () => {
    if (mySubscriptions.length > 0) {
      return mySubscriptions[0];
    }
    return currentSubscription;
  };

  // Check if a specific plan is currently active
  const isPlanActive = (planId) => {
    const activeSub = getLatestActiveSubscription();
    if (!activeSub) return false;
    return activeSub.planId === planId;
  };

  // Get the user's current plan details
  const getCurrentPlanDetails = () => {
    const activeSub = getLatestActiveSubscription();
    if (!activeSub || !plans.length) return null;
    
    const currentPlan = plans.find(plan => plan._id === activeSub.planId);
    return currentPlan;
  };

  // Handle plan purchase
  const handlePurchase = (plan) => {
    console.log('Selected plan for purchase:', plan);
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  // Handle successful payment
  const handlePaymentSuccess = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
    loadData();
  };

  // Close checkout modal
  const handleCloseCheckout = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
    setPurchasing(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get days remaining in subscription
  const getDaysRemaining = () => {
    const activeSub = getLatestActiveSubscription();
    if (!activeSub || activeSub.status !== 'active') return 0;
    
    const endDate = new Date(activeSub.endDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Get total amount paid for a subscription
  const getTotalAmountPaid = (subscription) => {
    if (!subscription.payments || subscription.payments.length === 0) return null;
    const total = subscription.payments.reduce((sum, payment) => sum + payment.amount, 0);
    return total;
  };

  // Toggle card expansion
  const toggleCardExpansion = (subscriptionId) => {
    setExpandedCards(prev => ({
      ...prev,
      [subscriptionId]: !prev[subscriptionId]
    }));
  };

  const currentPlan = getCurrentPlanDetails();
  const activeSubscription = getLatestActiveSubscription();
  const latestSubscription = getLatestSubscription();
  const daysRemaining = getDaysRemaining();

  if (loading) {
    return (
      <div className="plans-loading">
        <div className="loader"></div>
        <p>Loading your subscription plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="plans-error">
        <p>⚠️ {error}</p>
        <button onClick={loadData}>Try Again ↻</button>
      </div>
    );
  }

  return (
    <div className="plans-container">
      {/* Stripe Checkout Modal */}
      {showCheckout && selectedPlan && (
        <StripeCheckout
          plan={selectedPlan}
          subscriptionId={subscriptionId}
          onClose={handleCloseCheckout}
          onSuccess={handlePaymentSuccess}
        />
      )}
      
      <h1 className="plans-title">✨ Subscription Plans</h1>
      
      {/* Current Subscription Status */}
      {activeSubscription && (
        <div className={`current-subscription-banner ${activeSubscription.status}`}>
          <h2>🎯 Current Subscription</h2>
          <div className="subscription-details">
            <p><strong>📋 Plan:</strong> {currentPlan ? currentPlan.name.toUpperCase() : 'Unknown Plan'}</p>
            <p>
              <strong>📊 Status:</strong> 
              <span className={`status-badge ${activeSubscription.status}`}>
                {activeSubscription.status.toUpperCase()}
              </span>
            </p>
            {activeSubscription.status === 'active' && (
              <>
                <p><strong>📅 Start Date:</strong> {formatDate(activeSubscription.startDate)}</p>
                <p><strong>⏰ End Date:</strong> {formatDate(activeSubscription.endDate)}</p>
                <p><strong>⏳ Days Remaining:</strong> <strong style={{fontSize: '1.2rem'}}>{daysRemaining}</strong> days</p>
                {activeSubscription.payments && activeSubscription.payments.length > 0 && (
                  <p><strong>💵 Last Payment:</strong> ${activeSubscription.payments[activeSubscription.payments.length - 1].amount}</p>
                )}
              </>
            )}
            {activeSubscription.status === 'expired' && (
              <p className="expired-message">
                ⚠️ Your subscription expired on {formatDate(activeSubscription.endDate)}. 
                Please renew to continue enjoying benefits.
              </p>
            )}
          </div>
        </div>
      )}

      {/* No Active Subscription Message */}
      {!activeSubscription && (
        <div className="no-subscription-banner">
          <h2>🌟 No Active Subscription</h2>
          <p>You don't have an active subscription. Choose a plan below to get started!</p>
          {mySubscriptions.length > 0 && (
            <p className="last-subscription-note">
              📅 Your last subscription ended on {formatDate(mySubscriptions[0].endDate)}
            </p>
          )}
        </div>
      )}

      {/* Plans Grid */}
      <div className="plans-grid">
        {plans.map((plan) => {
          const isActive = isPlanActive(plan._id);
          const isExpired = activeSubscription && 
                           activeSubscription.planId === plan._id && 
                           activeSubscription.status === 'expired';
          
          return (
            <div 
              key={plan._id} 
              className={`plan-card ${isActive ? 'active-plan' : ''} ${isExpired ? 'expired-plan' : ''}`}
            >
              <div className="plan-header">
                <h2 className="plan-name">{plan.name.toUpperCase()}</h2>
                <div className="plan-price">
                  <span className="currency">$</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">/{plan.duration_in_days} days</span>
                </div>
              </div>
              
              <div className="plan-details">
                <p className="plan-description">{plan.description || 'Full access to all premium features'}</p>
                <div className="plan-meta">
                  <p>
                    <strong>⏱️ Duration:</strong>
                    <span>{plan.duration_in_days} days</span>
                  </p>
                  <p>
                    <strong>🏷️ Status:</strong>
                    <span className={`status-badge ${plan.status}`}>
                      {plan.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="plan-footer">
                {isActive ? (
                  <button className="btn btn-active" disabled>
                    ✓ Current Plan
                  </button>
                ) : isExpired ? (
                  <button 
                    className="btn btn-renew" 
                    onClick={() => handlePurchase(plan)}
                  >
                    🔄 Renew (${plan.price})
                  </button>
                ) : (
                  <button 
                    className="btn btn-primary" 
                    onClick={() => handlePurchase(plan)}
                  >
                    🚀 Subscribe (${plan.price})
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Enhanced Subscription History - Card View */}
      {mySubscriptions.length > 0 && (
        <div className="subscription-history">
          <h2>📜 Subscription History ({mySubscriptions.length} {mySubscriptions.length === 1 ? 'record' : 'records'})</h2>
          <div className="history-grid">
            {mySubscriptions.map((subscription, index) => {
              const planInfo = plans.find(p => p._id === subscription.planId);
              const totalAmount = getTotalAmountPaid(subscription);
              const paymentCount = subscription.payments?.length || 0;
              const isExpanded = expandedCards[subscription._id] || false;
              
              return (
                <div 
                  key={subscription._id} 
                  className={`history-card ${isExpanded ? 'expanded' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div 
                    className="history-card-header"
                    onClick={() => toggleCardExpansion(subscription._id)}
                  >
                    <div className="history-plan-name">
                      <span>{planInfo ? planInfo.name.toUpperCase() : 'Unknown Plan'}</span>
                      {paymentCount > 0 && (
                        <span className="payment-count">💰 {paymentCount} {paymentCount === 1 ? 'payment' : 'payments'}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                      <span className={`status-badge ${subscription.status}`}>
                        {subscription.status}
                      </span>
                      <span className="expand-icon">▼</span>
                    </div>
                  </div>
                  
                  <div className="history-card-body">
                    <div className="history-dates">
                      <div className="date-item">
                        <span className="date-label">Start Date</span>
                        <span className="date-value">📅 {formatDate(subscription.startDate)}</span>
                      </div>
                      <div className="date-item">
                        <span className="date-label">End Date</span>
                        <span className="date-value">⏰ {formatDate(subscription.endDate)}</span>
                      </div>
                    </div>
                    
                    {paymentCount > 0 && (
                      <div className="payments-section">
                        <div className="payments-title">💳 Payment History</div>
                        <div className="payments-list">
                          {subscription.payments.map((payment, idx) => (
                            <div key={payment._id || idx} className="payment-item">
                              <span className="payment-amount">${payment.amount}</span>
                              <span className="payment-date">{formatDateTime(payment.paymentDate)}</span>
                            </div>
                          ))}
                        </div>
                        {totalAmount && (
                          <div className="total-amount">
                            <span>💰 Total Paid:</span>
                            <strong style={{ color: 'var(--primary-color)' }}>${totalAmount.toFixed(2)}</strong>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {paymentCount === 0 && (
                      <div style={{ textAlign: 'center', color: 'var(--gray-600)', padding: '1rem' }}>
                        No payment records available
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;